import {
  canonicalizeLocaleTag,
  formatLocalizedCurrency,
  formatLocalizedDate,
  formatLocalizedDateTime,
  formatLocalizedNumber,
  formatLocalizedTime,
  normalizeTimeZone,
} from './localeExperience';
import {
  MESSAGE_CATALOGS,
  resolveMessageCatalogLocale,
  type LocalizedMessageCatalog,
  type MessageKey,
} from './catalogs';
import { interpolateMessage, type MessageValues } from './messageCatalog';

const FALLBACK_LOCALE = 'en-US';
const MISSING_MESSAGE_OPEN = '⟦';
const MISSING_MESSAGE_CLOSE = '⟧';

type LocalizedDateInput = Date | string | number | null | undefined;

export type MessageRuntimeKey<AppMessageKey extends string = never> = MessageKey | AppMessageKey;

export interface MessageRuntimeInput<AppMessageKey extends string = never> {
  locale: string;
  defaultLocale?: string | null;
  timeZone?: string | null;
  catalogs?: Readonly<Record<string, LocalizedMessageCatalog<MessageRuntimeKey<AppMessageKey>>>>;
  onMissingMessage?: (key: MessageRuntimeKey<AppMessageKey>) => string;
}

export interface MessageRuntime<AppMessageKey extends string = never> {
  locale: string;
  defaultLocale: string;
  fallbackLocale: string;
  timeZone: string;
  catalogChain: readonly string[];
  t: (key: MessageRuntimeKey<AppMessageKey>, values?: MessageValues) => string;
  formatNumber: (value: number | null | undefined, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (
    cents: number | null | undefined,
    currency: string,
    options?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>,
  ) => string;
  formatDate: (value: LocalizedDateInput, options?: Intl.DateTimeFormatOptions) => string;
  formatTime: (value: LocalizedDateInput, options?: Intl.DateTimeFormatOptions) => string;
  formatDateTime: (value: LocalizedDateInput, options?: Intl.DateTimeFormatOptions) => string;
}

function uniqueCatalogChain<AppMessageKey extends string>(
  requestedLocales: readonly (string | null | undefined)[],
  catalogs: Readonly<Record<string, LocalizedMessageCatalog<MessageRuntimeKey<AppMessageKey>>>>,
): readonly string[] {
  const result: string[] = [];
  const seen = new Set<string>();

  for (const requested of requestedLocales) {
    const catalogLocale = resolveMessageCatalogLocale(requested, catalogs);
    if (!catalogLocale) continue;
    const key = catalogLocale.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(catalogLocale);
  }

  return result;
}

/**
 * Create one framework-independent message and Intl runtime.
 *
 * Locale authority must be supplied by the application. This runtime never
 * reads `navigator.language`, browser preferences, storage, or the database.
 * Canonical English is always retained as the non-configurable final fallback.
 */
export function createMessageRuntime<const AppMessageKey extends string = never>(
  input: MessageRuntimeInput<AppMessageKey>,
): MessageRuntime<AppMessageKey> {
  const fallbackCatalog: LocalizedMessageCatalog<MessageRuntimeKey<AppMessageKey>> = Object.assign(
    {},
    input.catalogs?.[FALLBACK_LOCALE],
    MESSAGE_CATALOGS[FALLBACK_LOCALE],
  );

  const catalogs: Readonly<
    Record<string, LocalizedMessageCatalog<MessageRuntimeKey<AppMessageKey>>>
  > = {
    ...MESSAGE_CATALOGS,
    ...input.catalogs,
    [FALLBACK_LOCALE]: fallbackCatalog,
  };
  const fallbackLocale = FALLBACK_LOCALE;
  const defaultLocale = canonicalizeLocaleTag(input.defaultLocale) ?? fallbackLocale;
  const locale = canonicalizeLocaleTag(input.locale) ?? defaultLocale;
  const timeZone = normalizeTimeZone(input.timeZone);
  const catalogChain = uniqueCatalogChain([locale, defaultLocale, fallbackLocale], catalogs);

  const t = (key: MessageRuntimeKey<AppMessageKey>, values?: MessageValues): string => {
    for (const catalogLocale of catalogChain) {
      const template = catalogs[catalogLocale]?.[key];
      if (typeof template === 'string' && template.trim().length > 0) {
        return interpolateMessage(template, values);
      }
    }

    return input.onMissingMessage?.(key) ?? `${MISSING_MESSAGE_OPEN}${key}${MISSING_MESSAGE_CLOSE}`;
  };

  return {
    locale,
    defaultLocale,
    fallbackLocale,
    timeZone,
    catalogChain,
    t,
    formatNumber: (value, options) => formatLocalizedNumber(value, locale, options),
    formatCurrency: (cents, currency, options) =>
      formatLocalizedCurrency(cents, currency, locale, options),
    formatDate: (value, options = { dateStyle: 'medium' }) =>
      formatLocalizedDate(value, locale, timeZone, options),
    formatTime: (value, options = { timeStyle: 'short' }) =>
      formatLocalizedTime(value, locale, timeZone, options),
    formatDateTime: (value, options = { dateStyle: 'medium', timeStyle: 'short' }) =>
      formatLocalizedDateTime(value, locale, timeZone, options),
  };
}
